import { RefreshCw, Server, ShieldCheck } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button, Card, CardBody, CardHeader, DescriptionList, Spinner, StatusBadge } from '@/components/ui';
import { getApiHealth, getApiMeta, useQuery } from '@/data';
import { formatDateTime } from '@/lib/format';
import { AdminUsersCard } from './AdminUsersCard';
import { InventoryAppCard } from './InventoryAppCard';
import { MyAccountCard } from './MyAccountCard';

export function SettingsPage() {
  const { can } = useAuth();
  const health = useQuery(async () => {
    const [h, meta] = await Promise.all([getApiHealth(), getApiMeta()]);
    return { ...h, meta };
  }, []);

  return (
    <>
      <PageHeader title="Settings" subtitle="Account, admin users, Inventory App, and system status" />

      <div className="space-y-8">
        <section className="space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-ink">Account</h2>
            <p className="text-[13px] text-ink-secondary">Your signed-in Central Admin profile</p>
          </header>
          <div className="grid gap-6 xl:grid-cols-2">
            <MyAccountCard />
            <Card>
              <CardHeader
                title="Admin API"
                icon={<Server size={16} aria-hidden="true" />}
                description="Isolated Central Admin API and database (not the production backend)"
                actions={
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<RefreshCw size={14} aria-hidden="true" />}
                    onClick={health.reload}
                    disabled={health.loading || health.refreshing}
                  >
                    Check
                  </Button>
                }
              />
              <CardBody>
                {health.loading ? (
                  <p className="flex items-center gap-2 text-[13px] text-ink-secondary">
                    <Spinner size={16} /> Checking admin API…
                  </p>
                ) : health.error ? (
                  <DescriptionList
                    items={[
                      { label: 'Status', value: <StatusBadge tone="danger" label="Unreachable" /> },
                      { label: 'Details', value: <span className="text-[13px] text-ink-secondary">{health.error.message}</span> },
                    ]}
                  />
                ) : health.data ? (
                  <DescriptionList
                    items={[
                      {
                        label: 'Status',
                        value: (
                          <StatusBadge
                            tone={health.data.status === 'ok' ? 'success' : 'warning'}
                            label={health.data.status === 'ok' ? 'Healthy' : 'Degraded'}
                          />
                        ),
                      },
                      {
                        label: 'Database',
                        value: (
                          <StatusBadge
                            tone={health.data.database === 'ok' ? 'success' : 'danger'}
                            label={health.data.database === 'ok' ? 'Connected' : 'Unavailable'}
                          />
                        ),
                      },
                      { label: 'Version', value: health.data.version },
                      { label: 'Environment', value: health.data.meta.environment },
                      { label: 'Production integration', value: health.data.meta.productionIntegration ? 'Yes' : 'None (not linked)' },
                      { label: 'Checked', value: formatDateTime(health.data.timestamp) },
                    ]}
                  />
                ) : null}
              </CardBody>
            </Card>
          </div>
        </section>

        {can('admin.read') && (
          <section className="space-y-3">
            <header>
              <h2 className="text-sm font-semibold text-ink">Admin users</h2>
              <p className="text-[13px] text-ink-secondary">Roles and access for Central Admin staff</p>
            </header>
            <AdminUsersCard />
          </section>
        )}

        {can('inventory_app.read') && (
          <section className="space-y-3">
            <header>
              <h2 className="text-sm font-semibold text-ink">Inventory App</h2>
              <p className="text-[13px] text-ink-secondary">One global Inventory App for the whole platform — not per business</p>
            </header>
            <InventoryAppCard />
          </section>
        )}

        <section className="space-y-3">
          <header>
            <h2 className="text-sm font-semibold text-ink">Production boundary</h2>
            <p className="text-[13px] text-ink-secondary">What Central Admin stores vs Production</p>
          </header>
          <Card>
            <CardHeader title="Isolation rules" icon={<ShieldCheck size={16} aria-hidden="true" />} />
            <CardBody>
              <ul className="grid gap-2 text-[13px] text-ink-secondary sm:grid-cols-2">
                <li>• Central Admin has its own database; it never connects to the production POS database.</li>
                <li>• Businesses, branches, admin users and the audit trail are stored in Central Admin only.</li>
                <li>• Live Production ops (licenses, updates, API keys, devices, owners) run through the integration boundary.</li>
                <li>• Branch reference codes are Central Admin codes, not production branch references.</li>
              </ul>
            </CardBody>
          </Card>
        </section>
      </div>
    </>
  );
}
